import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { CreditCard, History, BarChart3, CheckCircle, XCircle, User, DollarSign, Calendar, TrendingUp } from 'lucide-react';
import AnimatedCounter from '../../components/common/AnimatedCounter';
import { toast } from '../../components/common/Toast';
import { payoutsAPI, getApiError } from '../../services/api';

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <motion.button
    onClick={onClick}
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-medium transition-all ${
      active ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
    }`}
  >
    <Icon className="w-4 h-4" />
    {label}
  </motion.button>
);

const RequestCard = ({ req, onApprove, onReject }) => {
  const { isDarkMode } = useTheme();
  return (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95, y: -20 }}
    whileHover={{ y: -4 }}
    className={`relative group ${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all overflow-hidden`}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className="p-6 relative">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white font-bold shadow-lg">
            {req.user_name?.charAt(0) || req.user_sttora_id?.charAt(0) || '?'}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{req.user_name || `User #${req.user_id}`}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{req.user_sttora_id || ''}</p>
          </div>
        </div>
        <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">${req.amount?.toFixed(2)}</span>
      </div>
      <div className="flex items-center gap-4 mt-4 text-sm text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{req.created_at ? new Date(req.created_at).toLocaleDateString() : ''}</span>
        <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />{req.method || 'bank_transfer'}</span>
      </div>
      <div className="flex gap-3 mt-4">
        <motion.button
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={() => onApprove(req.id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl text-sm font-medium shadow-lg shadow-green-500/20"
        >
          <CheckCircle className="w-4 h-4" /> Aprobar
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
          onClick={() => onReject(req.id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-2xl text-sm font-medium shadow-lg shadow-red-500/20"
        >
          <XCircle className="w-4 h-4" /> Rechazar
        </motion.button>
      </div>
    </div>
  </motion.div>
  );
};

const Payouts = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [tab, setTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState({ total_paid: 0, total_pending: 0, pending_count: 0, total_approved: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'requests') {
        const res = await payoutsAPI.getRequests({ status: 'pending' });
        setRequests(res.data || []);
      } else if (tab === 'history') {
        const res = await payoutsAPI.getHistory();
        setHistory(res.data || []);
      } else if (tab === 'summary') {
        const res = await payoutsAPI.getSummary();
        setSummary(res.data || { total_paid: 0, total_pending: 0, pending_count: 0, total_approved: 0 });
      }
    } catch (err) {
      toast.error(getApiError(err, 'Error al cargar datos'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [tab]);

  const handleApprove = async (id) => {
    try {
      await payoutsAPI.approveRequest(id);
      toast.success(`Solicitud #${id} aprobada`);
      fetchData();
    } catch (err) {
      toast.error(getApiError(err, 'Error al aprobar'));
    }
  };

  const handleReject = async (id) => {
    try {
      await payoutsAPI.rejectRequest(id);
      toast.info(`Solicitud #${id} rechazada`);
      fetchData();
    } catch (err) {
      toast.error(getApiError(err, 'Error al rechazar'));
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
        <h1 className={`text-3xl font-bold bg-gradient-to-r ${isDarkMode ? 'from-gray-100 to-gray-300' : 'from-gray-900 to-gray-600'} bg-clip-text text-transparent`}>
          {t('common.payouts')}
        </h1>
        <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Gestión de solicitudes de pago y aprobaciones</p>
      </motion.div>

      <div className="flex gap-3 flex-wrap">
        <TabButton active={tab === 'requests'} onClick={() => setTab('requests')} icon={CreditCard} label="Solicitudes" />
        <TabButton active={tab === 'history'} onClick={() => setTab('history')} icon={History} label="Historial" />
        <TabButton active={tab === 'summary'} onClick={() => setTab('summary')} icon={BarChart3} label="Resumen Financiero" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
        </div>
      ) : (
        <>
          {tab === 'requests' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {requests.map((req) => (
                  <RequestCard key={req.id} req={req} onApprove={handleApprove} onReject={handleReject} />
                ))}
              </AnimatePresence>
              {requests.length === 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="col-span-full text-center py-16">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">¡Todo al día!</h3>
                  <p className="text-gray-500 dark:text-gray-400">No hay solicitudes de pago pendientes</p>
                </motion.div>
              )}
            </div>
          )}

          {tab === 'history' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm overflow-hidden`}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <th className="text-left px-6 py-4 font-medium">Usuario</th>
                      <th className="text-left px-6 py-4 font-medium">Monto</th>
                      <th className="text-left px-6 py-4 font-medium">Fecha</th>
                      <th className="text-left px-6 py-4 font-medium">Método</th>
                      <th className="text-left px-6 py-4 font-medium">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((p, i) => (
                      <motion.tr key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'} hover:bg-orange-50/30 dark:hover:bg-orange-900/10 transition-colors`}
                      >
                        <td className="px-6 py-4 font-medium">{p.user_name || `User #${p.user_id}`}</td>
                        <td className="px-6 py-4 font-semibold">${p.amount?.toFixed(2)}</td>
                        <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</td>
                        <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{p.method || 'Transferencia'}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                            p.status === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                            'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          }`}>{p.status === 'completed' ? 'Completado' : p.status}</span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {tab === 'summary' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Total Pagado', value: summary.total_paid, icon: DollarSign, color: 'from-green-500 to-emerald-500' },
                { label: 'Pendiente por Pagar', value: summary.total_pending, icon: CreditCard, color: 'from-orange-500 to-red-500' },
                { label: 'Solicitudes Pendientes', value: summary.pending_count, icon: Calendar, color: 'from-blue-500 to-cyan-500' },
                { label: 'Total Aprobado', value: summary.total_approved, icon: TrendingUp, color: 'from-purple-500 to-pink-500' },
              ].map((item, i) => (
                <motion.div key={item.label} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }} whileHover={{ y: -4 }}
                  className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl p-6 border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm hover:shadow-xl transition-all`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</p>
                      <p className={`text-3xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {typeof item.value === 'number' && !Number.isInteger(item.value) ? (
                          <>$<AnimatedCounter value={item.value} decimals={2} /></>
                        ) : (
                          <AnimatedCounter value={item.value} />
                        )}
                      </p>
                    </div>
                    <div className={`p-3 rounded-2xl bg-gradient-to-r ${item.color} shadow-lg`}>
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default Payouts;
